import { masters } from "@nexseed/shared";
import { Building2 } from "lucide-react";
import { createElement } from "react";
import { FacilityPhotos } from "../components/facility-photos";
import { defineMaster, type MasterRow } from "../spec";

const status = { id: "isActive", header: "Status", kind: "status" } as const;
/** A primary line with a smaller muted line under it. */
const stacked = (top: string, bottom: string | null, mono = false) =>
  createElement(
    "div",
    { className: "flex flex-col leading-tight" },
    createElement("span", { className: "font-medium" }, top),
    bottom
      ? createElement(
          "span",
          {
            className: `text-xs text-muted-foreground${mono ? " font-mono" : ""}`,
          },
          bottom
        )
      : null
  );
const text = (v: unknown) => (typeof v === "string" && v ? v : null);
const typeLabel = (t: unknown) =>
  t === "internal" ? "Internal" : t === "external" ? "External" : text(t);
const activeSwitch = {
  name: "isActive",
  label: "Active",
  type: "switch",
} as const;

export const facilities = defineMaster({
  key: "facilities",
  title: "Processing Plant",
  singular: "processing plant",
  description:
    "Processing plants and third-party drying facilities. The plant code prefixes every lot minted there and ends its document numbers; warehouses hang off it.",
  icon: Building2,
  schema: masters.facility.create,
  searchPlaceholder: "Search name or plant code",
  defaultSort: "name",
  // Forms show the full name; the plant switcher keeps the short one.
  optionLabel: (r: MasterRow) =>
    text(r.fullName) ?? `${String(r.plantCode)} · ${String(r.name)}`,
  columns: [
    {
      id: "name",
      header: "Plant",
      sortable: true,
      cell: (r) => stacked(text(r.name) ?? "", text(r.plantCode), true),
    },
    {
      id: "isDryingFacility",
      header: "Facility",
      cell: (r) =>
        stacked(
          r.isDryingFacility ? "Drying facility" : "Processing plant",
          typeLabel(r.type)
        ),
    },
    { id: "city", header: "City" },
    status,
  ],
  fields: [
    {
      name: "plantCode",
      label: "Plant code",
      type: "text",
      transform: "upper",
      required: true,
      description: "1–3 letters; prefixes every lot number",
    },
    { name: "name", label: "Name", type: "text", required: true },
    {
      name: "isDryingFacility",
      label: "Drying facility",
      type: "switch",
      description: "A third-party dryer on the cob chain",
    },
    {
      name: "enforceBinCapacity",
      label: "Enforce bin capacity",
      type: "switch",
      description:
        "Refuse any stock posting that would put more into a bin than its capacity (kg). Bins with no capacity are never limited.",
    },
    {
      name: "type",
      label: "Type",
      type: "enum",
      options: [
        { value: "internal", label: "Internal" },
        { value: "external", label: "External" },
      ],
    },
    {
      name: "scaptureMappingCode",
      label: "S-Capture mapping code",
      type: "text",
    },
    { name: "iecCode", label: "IEC code", type: "text" },
    {
      name: "fullName",
      label: "Full name (on forms)",
      type: "text",
      colSpan: 2,
      description:
        "Shown on forms and printed notes, e.g. VNR Seeds Private Limited-Boriya(Plant). The plant switcher keeps the short Name. Blank → Name.",
    },
    { name: "address", label: "Address", type: "textarea", colSpan: 2 },
    {
      name: "printAddress",
      label: "Address on prints (Plant: line)",
      type: "textarea",
      colSpan: 2,
      description:
        'Printed exactly as typed after "Plant:" on every note of this plant. Blank → built from Address, City, State and PIN.',
    },
    { name: "city", label: "City", type: "text" },
    { name: "state", label: "State", type: "text" },
    { name: "pincode", label: "PIN code", type: "text" },
    { name: "country", label: "Country", type: "text" },
    {
      name: "gstin",
      label: "GSTIN",
      type: "text",
      transform: "upper",
      description: "Printed on this plant's dispatch notes.",
    },
    activeSwitch,
  ],
  sheetSection: (row) => createElement(FacilityPhotos, { facilityId: row?.id }),
});
